import { useCallback, useEffect, useState } from 'react';
import { CategoryPageHeader } from './CategoryPageHeader';
import { SearchInput } from '../publications/form/SearchInput';
import { CategoryCreateModal } from './Modals/CategoryCreateModal';
import { useSearch } from '../hooks/useSearch';
import { CategoryUpdateModal } from './Modals/CategoryUpdateModal';
import { CategoryArchiveModal } from './Modals/CategoryArchiveModal';
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { CategoryCard } from './CategoryCard';
import Spinner from '../spinner/Spinner';
import {
    Category,
    CategoryStatus,
    createCategory,
    getCategories,
    postCategoryMedia,
    reOrderCategory,
    updateCategory,
} from '../api/category';
import { imageSrc, mediaExtension, mediaUrlExtension, updateMedia } from '../api/media';
import { EmptyCategory } from './EmptyCategory';
import { useCategoryPublicationsMap } from './hooks/useCategoryPublicationsMap';
import { useCategoryModalFileUpload } from './hooks/useCategoryModalFileUpload';
import { ALLOWED_CATEGORY_MEDIA_EXTENSION_TYPE } from './categoryMediaConstants';
import { revokePreviewObjectUrl } from '../utils/objectUrl';
import { PageLayout } from '../layout/PageLayout';
import { IconButton, ButtonLink } from '../components/Button';
import addCircleIcon from '../assets/add-circle.svg';
import { Option } from '../types/option';
import { getCategoryFlowErrorMessage } from '../api/problemJson';
import { AlertBanner } from '../components/AlertBanner';

const CATEGORY_MODAL_API_ERROR_MESSAGE = 'Un problème est survenu.';

export function Categories() {
    const [categories, setCategories] = useState<Category[]>([]);
    const {
        publicationsByCategory,
        loadPublicationsForCategory,
        refreshPublicationsForCategory,
        isCategoryLoading,
        getPublicationsLoadError,
    } = useCategoryPublicationsMap();

    const handleCategories = useCallback(async (search: Option<string>) => {
        setIsLoading(true);
        setListApiError(Option.none());

        try {
            const categoriesAPI = await getCategories(search);
            setCategories(categoriesAPI);
        } catch (error: unknown) {
            setListApiError(
                Option.some(getCategoryFlowErrorMessage(error, CATEGORY_MODAL_API_ERROR_MESSAGE)),
            );
        } finally {
            setIsLoading(false);
        }
    }, []);

    const reloadFullList = useCallback(() => {
        void handleCategories(Option.none());
    }, [handleCategories]);

    const searchCategories = useCallback(
        (query: string) => handleCategories(Option.some(query)),
        [handleCategories],
    );

    const [search, isSearching, handleSearchText] = useSearch(
        searchCategories,
        3,
        300,
        reloadFullList,
    );

    const [currentUpdatingCategory, setCurrentUpdatingCategory] = useState<Option<Category>>(
        Option.none(),
    );
    const [currentArchiveCategory, setCurrentArchiveCategory] = useState<Option<Category>>(
        Option.none(),
    );

    const [title, setTitle] = useState<string>('');
    const [mediaPreviewUrl, setMediaPreviewUrl] = useState<Option<string>>(Option.none());
    const [mediaPreviewExtension, setMediaPreviewExtension] = useState<Option<string>>(
        Option.none(),
    );
    const [selectedMediaFile, setSelectedMediaFile] = useState<Option<File>>(Option.none());

    const [isUpdateModalOpened, setIsUpdateModalOpened] = useState<boolean>(false);
    const [isCreateModalOpened, setIsCreateModalOpened] = useState<boolean>(false);
    const [isDeleteModalOpened, setIsArchiveModalOpened] = useState<boolean>(false);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fileFormatMessage, setFileFormatMessage] = useState<string>('');
    const [categoryModalApiError, setCategoryModalApiError] = useState<Option<string>>(
        Option.none(),
    );
    const [archiveModalApiError, setArchiveModalApiError] = useState<Option<string>>(Option.none());
    const [listApiError, setListApiError] = useState<Option<string>>(Option.none());

    const sensors = useSensors(useSensor(PointerSensor));

    useEffect(() => {
        void handleCategories(Option.none());
    }, [handleCategories]);

    const resetMedia = useCallback(() => {
        mediaPreviewUrl.map(revokePreviewObjectUrl);
        setMediaPreviewUrl(Option.none());
        setMediaPreviewExtension(Option.none());
        setSelectedMediaFile(Option.none());
    }, [mediaPreviewUrl]);

    const onFileInvalid = useCallback((userMessage: string) => {
        setFileFormatMessage(userMessage);
        window.setTimeout(() => setFileFormatMessage(''), 5000);
    }, []);

    const onUploaded = useCallback(
        (file: File) => {
            mediaPreviewUrl.map(revokePreviewObjectUrl);
            setSelectedMediaFile(Option.some(file));
            setMediaPreviewUrl(Option.some(URL.createObjectURL(file)));
            setMediaPreviewExtension(Option.some(mediaUrlExtension(file.name)));
        },
        [mediaPreviewUrl],
    );

    const { handleMediaChange } = useCategoryModalFileUpload({
        allowedMimeTypes: ALLOWED_CATEGORY_MEDIA_EXTENSION_TYPE,
        onUploaded,
        onInvalidType: onFileInvalid,
        onClear: resetMedia,
    });

    const handleCloseCreateModal = useCallback(() => {
        setTitle('');
        resetMedia();
        setCategoryModalApiError(Option.none());
        setIsCreateModalOpened(false);
    }, [resetMedia]);

    const handleCloseUpdateModal = useCallback(() => {
        resetMedia();
        setTitle('');
        setCategoryModalApiError(Option.none());
        setCurrentUpdatingCategory(Option.none());
        setIsUpdateModalOpened(false);
    }, [resetMedia]);

    const handleCloseDeleteModal = useCallback(() => {
        setArchiveModalApiError(Option.none());
        setCurrentArchiveCategory(Option.none());
        setIsArchiveModalOpened(false);
    }, []);

    const handleCreate = useCallback(async () => {
        await selectedMediaFile
            .map(async file => {
                try {
                    const created = await createCategory({ title, status: CategoryStatus.ACTIVE });
                    await postCategoryMedia(created.id, file);
                    await handleCategories(Option.none());
                    handleCloseCreateModal();
                } catch (error: unknown) {
                    setCategoryModalApiError(
                        Option.some(
                            getCategoryFlowErrorMessage(error, CATEGORY_MODAL_API_ERROR_MESSAGE),
                        ),
                    );
                }
            })
            .getOrElse(Promise.resolve());
    }, [title, selectedMediaFile, handleCategories, handleCloseCreateModal]);

    const handleDelete = useCallback(async () => {
        await currentArchiveCategory
            .map(async category => {
                setArchiveModalApiError(Option.none());

                try {
                    await updateCategory(category.id, {
                        title: category.title,
                        status: CategoryStatus.ARCHIVE,
                    });
                    await handleCategories(Option.none());
                    handleCloseDeleteModal();
                } catch (error: unknown) {
                    setArchiveModalApiError(
                        Option.some(
                            getCategoryFlowErrorMessage(error, CATEGORY_MODAL_API_ERROR_MESSAGE),
                        ),
                    );
                }
            })
            .getOrElse(Promise.resolve());
    }, [currentArchiveCategory, handleCategories, handleCloseDeleteModal]);

    const handleUpdate = useCallback(async () => {
        await currentUpdatingCategory
            .map(async category => {
                try {
                    await updateCategory(category.id, { title, status: category.status });
                    await selectedMediaFile
                        .map(async file => {
                            const updateExistingMedia = category.media.map(async media =>
                                updateMedia(media.id, file, 0),
                            );

                            if (updateExistingMedia.isDefined()) {
                                await updateExistingMedia.get();
                                return;
                            }

                            await postCategoryMedia(category.id, file);
                        })
                        .getOrElse(Promise.resolve());

                    setTitle('');
                    resetMedia();
                    setCategoryModalApiError(Option.none());
                    await handleCategories(Option.none());
                    setCurrentUpdatingCategory(Option.none());
                    setIsUpdateModalOpened(false);
                } catch (error: unknown) {
                    setCategoryModalApiError(
                        Option.some(
                            getCategoryFlowErrorMessage(error, CATEGORY_MODAL_API_ERROR_MESSAGE),
                        ),
                    );
                }
            })
            .getOrElse(Promise.resolve());
    }, [title, selectedMediaFile, currentUpdatingCategory, handleCategories, resetMedia]);

    const handleDragEnd = useCallback(
        async (event: DragEndEvent) => {
            const { active, over } = event;

            if (!over || active.id === over.id) {
                return;
            }

            const oldIndex = categories.findIndex(t => t.id === active.id);
            const newIndex = categories.findIndex(t => t.id === over.id);

            if (oldIndex === -1 || newIndex === -1) {
                return;
            }

            const updatedCategories = arrayMove(categories, oldIndex, newIndex).map(
                (task, index) => ({
                    ...task,
                    priority: index + 1,
                }),
            );

            setCategories(updatedCategories);

            try {
                const payload = updatedCategories.map(({ id, priority }) => ({ id, priority }));
                await reOrderCategory(payload);
                setListApiError(Option.none());
            } catch (error: unknown) {
                setCategories(categories);
                setListApiError(
                    Option.some(
                        getCategoryFlowErrorMessage(error, CATEGORY_MODAL_API_ERROR_MESSAGE),
                    ),
                );
            }
        },
        [categories],
    );

    const openUpdateForCategory = (c: Category) => {
        setCategoryModalApiError(Option.none());
        setTitle(c.title);
        setSelectedMediaFile(Option.none());
        c.media.fold(
            () => {
                setMediaPreviewUrl(Option.none());
                setMediaPreviewExtension(Option.none());
            },
            media => {
                setMediaPreviewUrl(Option.some(imageSrc(media)));
                setMediaPreviewExtension(mediaExtension(c.media));
            },
        );
        setCurrentUpdatingCategory(Option.some(c));
        setIsUpdateModalOpened(true);
    };

    const openArchiveForCategory = (c: Category) => {
        setArchiveModalApiError(Option.none());
        setCurrentArchiveCategory(Option.some(c));
        setIsArchiveModalOpened(true);
    };

    const openCreateModal = () => {
        setCategoryModalApiError(Option.none());
        setIsCreateModalOpened(true);
    };

    const listLoading = isLoading || isSearching;

    return (
        <>
            {fileFormatMessage && (
                <p
                    className="fixed bottom-6 left-1/2 z-[1001] -translate-x-1/2 rounded-3xl bg-red-500 px-4 py-2 text-sm font-medium text-white shadow-lg"
                    role="status"
                >
                    {fileFormatMessage}
                </p>
            )}
            <PageLayout>
                <CategoryPageHeader
                    actions={
                        <>
                            <ButtonLink to="/archived_categories" isPrimary={false}>
                                Mes catégories archivées
                            </ButtonLink>
                            <IconButton
                                label="Créer une catégorie"
                                onClick={openCreateModal}
                                icon={addCircleIcon}
                            />
                        </>
                    }
                />
                {listApiError
                    .map(message => <AlertBanner className="mb-4 w-full">{message}</AlertBanner>)
                    .toNull()}
                <div className="my-6 w-full">
                    <SearchInput
                        search={search}
                        handleSearch={handleSearchText}
                        placeholder="Rechercher une categorie..."
                    />
                </div>
                <Spinner loading={listLoading} />
                {!listLoading && categories.length === 0 && (
                    <EmptyCategory
                        className="shadow-md"
                        title="Aucune catégorie"
                        description="Commencer la génération de votre bibliothèque en cliquant sur le bouton ci-dessous."
                        action={
                            <IconButton
                                label={'Créer une catégorie'}
                                onClick={openCreateModal}
                                icon={addCircleIcon}
                            />
                        }
                    />
                )}
                {!listLoading && categories.length > 0 && (
                    <ul className="flex flex-col gap-4">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                            modifiers={[restrictToParentElement]}
                        >
                            <SortableContext
                                items={categories.map(t => t.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {categories.map(c => (
                                    <CategoryCard
                                        key={c.id}
                                        category={c}
                                        publications={Option.some(publicationsByCategory.get(c.id))}
                                        loadingCategoryPublication={isCategoryLoading(c.id)}
                                        publicationsLoadError={getPublicationsLoadError(c.id)}
                                        onUpdate={openUpdateForCategory}
                                        onArchive={openArchiveForCategory}
                                        onLoadPublications={loadPublicationsForCategory}
                                        onRefreshPublications={refreshPublicationsForCategory}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    </ul>
                )}
            </PageLayout>
            <CategoryCreateModal
                isOpen={isCreateModalOpened}
                title={title}
                media={mediaPreviewUrl.getOrElse('')}
                extension={mediaPreviewExtension.getOrElse('')}
                handleTitleChange={setTitle}
                handleMediaChange={handleMediaChange}
                handleResetMedia={resetMedia}
                handleClose={handleCloseCreateModal}
                handleConfirm={handleCreate}
                apiError={categoryModalApiError}
            />
            {currentUpdatingCategory
                .map(() => (
                    <CategoryUpdateModal
                        isOpen={isUpdateModalOpened}
                        title={title}
                        media={mediaPreviewUrl.getOrElse('')}
                        extension={mediaPreviewExtension.getOrElse('')}
                        handleTitleChange={setTitle}
                        handleMediaChange={handleMediaChange}
                        handleResetMedia={resetMedia}
                        handleClose={handleCloseUpdateModal}
                        handleConfirm={handleUpdate}
                        apiError={categoryModalApiError}
                    />
                ))
                .toNull()}
            {currentArchiveCategory
                .map(category => (
                    <CategoryArchiveModal
                        isOpen={isDeleteModalOpened}
                        category={category}
                        handleClose={handleCloseDeleteModal}
                        handleConfirm={handleDelete}
                        apiError={archiveModalApiError}
                    />
                ))
                .toNull()}
        </>
    );
}
